// What a run is called where an agent's prose or a record links to it: the conversation's
// title, or which pass it was. One list read per page feeds every link through context,
// never a fetch per link (principle 8).
import { createContext, useContext, useMemo } from "react";

import { type StatefulRun, useStatefulRuns } from "./useStatefulAgents";
import { formatDay } from "../../utils/attributeFormat";

const RunNamesContext = createContext<ReadonlyMap<string, string>>(new Map());

export const RunNamesProvider = RunNamesContext.Provider;

/** Run names by lower-case run id; empty outside a provider, so links keep the id. */
export const useRunNameMap = (): ReadonlyMap<string, string> =>
  useContext(RunNamesContext);

/** The run's name for a link; "" when it is not in the list the page read. */
export const useRunName = (runId: string): string =>
  useRunNameMap().get(runId.toLowerCase()) ?? "";

/** Every run of a ``projectId`` the viewer may see, by id; read only when ``enabled``. */
export const useRunNames = (
  projectId: string,
  enabled: boolean,
): ReadonlyMap<string, string> => {
  const { data } = useStatefulRuns(enabled ? projectId : undefined);
  return useMemo(
    () =>
      new Map(
        (data?.runs ?? []).map((run) => [
          run.run_id.toLowerCase(),
          runName(run),
        ]),
      ),
    [data],
  );
};

/** What a run is called: its title, or which pass or conversation it was. The one rule
 *  every link to a run uses. */
export const runName = (
  run: Pick<StatefulRun, "title" | "kind" | "created_at">,
): string =>
  run.title.trim() ||
  (run.kind === "scheduled"
    ? `the ${formatDay(run.created_at)} pass`
    : `a conversation of ${formatDay(run.created_at)}`);
